import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import Button from '../../components/ui/Button';
import { getAgingReport, type AgingFigures, type AgingReport } from '../../lib/accountsPayableReportsApi';
import { downloadObjectsCsv } from '../../utils/downloadCsv';
import AgingTable from './AgingTable';
import { formatReportDay, supplierLabel } from './format';
import { IndicativeLine, ReportFrame } from './ReportFrame';
import { useReport } from './useReport';
import DateInput from "../../components/DateInput";

/**
 * Aging (D49 phase 2): what is owed, by supplier and by how late, each currency in its own
 * table. By DUE date, measured in whole days from the "aged as of" day.
 *
 * The as-of day may be today or later. It ages TODAY'S balances from that day ("what will be
 * overdue on the 30th if nothing is paid"): a bill stores only what it owes now, so a past day
 * cannot be reconstructed, and the page says so instead of letting a date box suggest otherwise.
 */

const FIELD_INPUT =
  'h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20';

/** The rows of the CSV: one per supplier per currency, then a total row per currency. */
export function agingCsvRows(report: AgingReport): Array<Record<string, unknown>> {
  const rows: Array<Record<string, unknown>> = [];
  const figuresOf = (figures: AgingFigures) => {
    const out: Record<string, unknown> = {};
    for (const column of report.buckets) out[column.label] = figures[column.key];
    out.Bills = figures.billCount;
    return out;
  };
  for (const block of report.currencies) {
    for (const supplier of block.suppliers) {
      rows.push({ Currency: block.currency, Supplier: supplierLabel(supplier.supplierName), ...figuresOf(supplier) });
    }
    rows.push({ Currency: block.currency, Supplier: 'All suppliers', ...figuresOf(block.totals) });
  }
  return rows;
}

export const AgingReportView: React.FC = () => {
  // Blank = today. The server answers with today's date, which is also the earliest day the
  // picker offers, so the browser never works out "today" (a Trinidad evening is already
  // tomorrow in UTC).
  const [asOf, setAsOf] = useState('');
  const [today, setToday] = useState('');

  const state = useReport(async () => {
    const res = await getAgingReport({ asOf });
    return res.data;
  }, [asOf]);

  const report = state.data;
  useEffect(() => {
    if (report && !asOf) setToday(report.asOf);
  }, [report, asOf]);

  const csvRows = useMemo(() => (report ? agingCsvRows(report) : []), [report]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Age the balances as of</span>
          <DateInput validity="filter"
            min={today || undefined}
            value={asOf || (report?.asOf ?? '')}
            onChange={(v) => setAsOf(v)}
            className={FIELD_INPUT}
          />
        </label>
        {asOf ? (
          <Button size="sm" onClick={() => setAsOf('')}>
            Back to today
          </Button>
        ) : null}
        <div className="ml-auto">
          <Button
            variant="primary"
            icon={<Download aria-hidden="true" className="h-4 w-4" />}
            disabled={csvRows.length === 0}
            onClick={() => downloadObjectsCsv(`ap-aging-${report?.asOf ?? 'today'}.csv`, csvRows)}
          >
            Export CSV
          </Button>
        </div>
      </div>
      <p className="mb-4 max-w-[80ch] text-xs text-slate-600">
        Balances are as they stand now; only the age is measured from the date you pick. Each bill is aged by
        its due date: a bill due on the day you pick is not yet late, and one on hold or disputed is owed but is
        never counted as due. Bills with no due date have their own column.
      </p>

      <ReportFrame
        state={state}
        isEmpty={(data) => data.currencies.length === 0}
        emptyTitle="Nothing is owed to suppliers."
        emptyDetail="Every bill is paid or credited in full."
      >
        {(data) => (
          <>
            <p className="mb-3 text-xs text-slate-500">Aged as of {formatReportDay(data.asOf)}.</p>
            {data.currencies.map((block) => (
              <AgingTable key={block.currency} report={data} block={block} />
            ))}
            <IndicativeLine
              indicative={data.indicative}
              figureKey="total"
              currencyCount={data.currencies.length}
              what="total owed"
            />
          </>
        )}
      </ReportFrame>
    </div>
  );
};

export default AgingReportView;
